import * as THREE from 'three';

/**
 * AI行为状态
 */
export enum AIState {
  IDLE = 'idle',           // 待机
  CHASE = 'chase',         // 追击
  RETREAT = 'retreat',     // 撤退
  STRAFE = 'strafe',       // 侧向移动
  AMBUSH = 'ambush',       // 埋伏
  COVER = 'cover'          // 寻找掩护
}

/**
 * AI战术类型
 */
export enum AITactic {
  AGGRESSIVE = 'aggressive',   // 激进型（重型坦克）
  BALANCED = 'balanced',       // 平衡型（普通坦克）
  EVASIVE = 'evasive',        // 闪避型（轻型坦克）
  SNIPER = 'sniper'           // 狙击型（狙击坦克）
}

/**
 * 障碍物信息
 */
interface Obstacle {
  position: THREE.Vector3;
  radius: number;
}

/**
 * AI控制器
 */
export class AIController {
  private state: AIState = AIState.IDLE;
  private tactic: AITactic;
  private stateTimer: number = 0;
  private obstacles: Obstacle[] = [];

  // 战术参数
  private preferredDistance: number = 10;  // 首选作战距离
  private retreatDistance: number = 5;      // 撤退距离
  private chargeDistance: number = 15;      // 冲锋距离

  // 侧向移动
  private strafeDirection: number = 1;      // 1 或 -1
  private strafeTimer: number = 0;

  // 预测
  private lastPlayerPosition: THREE.Vector3 = new THREE.Vector3();
  private playerVelocity: THREE.Vector3 = new THREE.Vector3();

  constructor(tactic: AITactic) {
    this.tactic = tactic;
    this.initTacticParameters();
  }

  /**
   * 初始化战术参数
   */
  private initTacticParameters(): void {
    switch (this.tactic) {
      case AITactic.AGGRESSIVE:
        // 重型坦克：近战，冲锋
        this.preferredDistance = 8;
        this.retreatDistance = 3;
        this.chargeDistance = 20;
        break;

      case AITactic.BALANCED:
        // 普通坦克：中距离
        this.preferredDistance = 12;
        this.retreatDistance = 5;
        this.chargeDistance = 15;
        break;

      case AITactic.EVASIVE:
        // 轻型坦克：远距离，机动
        this.preferredDistance = 15;
        this.retreatDistance = 8;
        this.chargeDistance = 25;
        break;

      case AITactic.SNIPER:
        // 狙击坦克：超远距离，保持距离
        this.preferredDistance = 20;
        this.retreatDistance = 12;
        this.chargeDistance = 30;
        break;
    }
  }

  /**
   * 设置障碍物列表
   */
  setObstacles(obstacles: Obstacle[]): void {
    this.obstacles = obstacles;
  }

  /**
   * 更新AI状态和决策
   */
  update(
    deltaTime: number,
    enemyPosition: THREE.Vector3,
    playerPosition: THREE.Vector3,
    enemyHealth: number,
    maxHealth: number
  ): { forward: number; turn: number; shouldShoot: boolean } {
    // 更新状态计时器
    this.stateTimer += deltaTime;

    // 计算玩家速度（用于预测）
    this.playerVelocity.copy(playerPosition).sub(this.lastPlayerPosition).divideScalar(deltaTime);
    this.lastPlayerPosition.copy(playerPosition);

    // 计算到玩家的距离
    const distanceToPlayer = enemyPosition.distanceTo(playerPosition);

    // 决定AI状态
    this.decideState(distanceToPlayer, enemyHealth, maxHealth);

    // 根据状态执行行为
    const decision = this.executeState(enemyPosition, playerPosition, distanceToPlayer, deltaTime);

    return decision;
  }

  /**
   * 决定AI状态
   */
  private decideState(distance: number, health: number, maxHealth: number): void {
    const healthRatio = health / maxHealth;

    // 如果生命值低，考虑撤退
    if (healthRatio < 0.3 && this.tactic !== AITactic.AGGRESSIVE) {
      if (this.state !== AIState.RETREAT) {
        this.state = AIState.RETREAT;
        this.stateTimer = 0;
      }
      return;
    }

    // 根据距离和战术类型决定状态
    switch (this.tactic) {
      case AITactic.AGGRESSIVE:
        // 激进型：总是冲锋
        if (distance > this.preferredDistance) {
          this.changeState(AIState.CHASE);
        } else {
          this.changeState(AIState.CHASE); // 继续接近
        }
        break;

      case AITactic.BALANCED:
        // 平衡型：保持首选距离
        if (distance > this.chargeDistance) {
          this.changeState(AIState.CHASE);
        } else if (distance < this.retreatDistance) {
          this.changeState(AIState.RETREAT);
        } else if (Math.abs(distance - this.preferredDistance) < 3) {
          this.changeState(AIState.STRAFE); // 保持距离并侧向移动
        } else if (distance > this.preferredDistance) {
          this.changeState(AIState.CHASE);
        } else {
          this.changeState(AIState.RETREAT);
        }
        break;

      case AITactic.EVASIVE:
        // 闪避型：频繁侧向移动
        if (distance > this.chargeDistance) {
          this.changeState(AIState.CHASE);
        } else if (distance < this.retreatDistance) {
          this.changeState(AIState.RETREAT);
        } else {
          // 大部分时间在侧向移动
          if (this.stateTimer > 1.5) {
            this.changeState(AIState.STRAFE);
          }
        }
        break;

      case AITactic.SNIPER:
        // 狙击型：保持远距离
        if (distance > this.chargeDistance) {
          this.changeState(AIState.CHASE);
        } else if (distance < this.preferredDistance) {
          this.changeState(AIState.RETREAT);
        } else {
          this.changeState(AIState.AMBUSH); // 伏击位置
        }
        break;
    }
  }

  /**
   * 改变状态
   */
  private changeState(newState: AIState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.stateTimer = 0;

      // 侧向移动时随机选择方向
      if (newState === AIState.STRAFE) {
        this.strafeDirection = Math.random() < 0.5 ? -1 : 1;
      }
    }
  }

  /**
   * 执行当前状态
   */
  private executeState(
    enemyPosition: THREE.Vector3,
    playerPosition: THREE.Vector3,
    distance: number,
    deltaTime: number
  ): { forward: number; turn: number; shouldShoot: boolean } {
    let targetPosition = playerPosition.clone();

    // 预测玩家位置（提前0.5秒）
    if (this.tactic === AITactic.SNIPER) {
      targetPosition.add(this.playerVelocity.clone().multiplyScalar(0.5));
    }

    // 检查是否需要避开障碍物
    const avoidanceDirection = this.getObstacleAvoidance(enemyPosition, targetPosition);

    let forward = 0;
    let turn = 0;
    let shouldShoot = false;

    // 计算朝向目标的角度
    const directionToTarget = new THREE.Vector3()
      .subVectors(targetPosition, enemyPosition)
      .normalize();

    // 如果有避障方向，混合进去
    if (avoidanceDirection) {
      directionToTarget.lerp(avoidanceDirection, 0.7);
    }

    const angleToTarget = Math.atan2(directionToTarget.x, directionToTarget.z);

    switch (this.state) {
      case AIState.CHASE:
        // 追击：前进并面向玩家
        forward = 1;
        turn = this.calculateTurnDirection(angleToTarget);
        shouldShoot = distance < 30 && Math.random() < 0.1;
        break;

      case AIState.RETREAT:
        // 撤退：后退并面向玩家
        forward = -1;
        turn = this.calculateTurnDirection(angleToTarget);
        shouldShoot = Math.random() < 0.05;
        break;

      case AIState.STRAFE:
        // 侧向移动
        this.strafeTimer += deltaTime;
        if (this.strafeTimer > 2) {
          this.strafeTimer = 0;
          this.strafeDirection *= -1; // 改变侧向方向
        }

        // 面向玩家
        turn = this.calculateTurnDirection(angleToTarget);

        // 侧向移动（通过前进+转向组合实现）
        forward = 0.5;
        turn += this.strafeDirection * 0.3;

        shouldShoot = Math.random() < 0.15;
        break;

      case AIState.AMBUSH:
        // 伏击：静止不动，瞄准射击
        forward = 0;
        turn = this.calculateTurnDirection(angleToTarget) * 0.5; // 缓慢转向
        shouldShoot = Math.random() < 0.2; // 较高射击频率
        break;

      case AIState.COVER:
        // 寻找掩护（简化：向最近的障碍物移动）
        const nearestObstacle = this.findNearestObstacle(enemyPosition);
        if (nearestObstacle) {
          const directionToObstacle = new THREE.Vector3()
            .subVectors(nearestObstacle.position, enemyPosition)
            .normalize();
          const angleToObstacle = Math.atan2(directionToObstacle.x, directionToObstacle.z);
          forward = 1;
          turn = this.calculateTurnDirection(angleToObstacle);
        } else {
          // 没有障碍物，切换到撤退
          this.state = AIState.RETREAT;
        }
        shouldShoot = Math.random() < 0.05;
        break;

      case AIState.IDLE:
      default:
        forward = 0;
        turn = 0;
        shouldShoot = false;
        break;
    }

    return { forward, turn, shouldShoot };
  }

  /**
   * 计算转向方向（-1 到 1）
   */
  private calculateTurnDirection(targetAngle: number): number {
    // 简化：直接返回角度的符号
    // 实际应该考虑当前角度
    return Math.sign(targetAngle);
  }

  /**
   * 获取障碍物避让方向
   */
  private getObstacleAvoidance(
    position: THREE.Vector3,
    targetPosition: THREE.Vector3
  ): THREE.Vector3 | null {
    const directionToTarget = new THREE.Vector3()
      .subVectors(targetPosition, position)
      .normalize();

    // 检测前方的障碍物
    const detectionDistance = 8;
    const avoidanceForce = new THREE.Vector3();
    let obstacleCount = 0;

    for (const obstacle of this.obstacles) {
      const toObstacle = new THREE.Vector3()
        .subVectors(obstacle.position, position);
      const distanceToObstacle = toObstacle.length();

      // 只考虑前方的障碍物
      const dot = toObstacle.normalize().dot(directionToTarget);
      if (dot > 0.3 && distanceToObstacle < detectionDistance + obstacle.radius) {
        // 计算避让力
        const avoidStrength = 1 - (distanceToObstacle / detectionDistance);
        const avoidDir = position.clone().sub(obstacle.position).normalize();
        avoidanceForce.add(avoidDir.multiplyScalar(avoidStrength));
        obstacleCount++;
      }
    }

    if (obstacleCount > 0) {
      return avoidanceForce.normalize();
    }

    return null;
  }

  /**
   * 寻找最近的障碍物
   */
  private findNearestObstacle(position: THREE.Vector3): Obstacle | null {
    let nearest: Obstacle | null = null;
    let minDistance = Infinity;

    for (const obstacle of this.obstacles) {
      const distance = position.distanceTo(obstacle.position);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = obstacle;
      }
    }

    return nearest;
  }

  /**
   * 获取当前状态
   */
  getState(): AIState {
    return this.state;
  }

  /**
   * 获取战术类型
   */
  getTactic(): AITactic {
    return this.tactic;
  }
}
